import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Pencil, RotateCcw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DEFAULT_ANNOUNCEMENT_BACKGROUND, DEFAULT_ANNOUNCEMENT_TEXT } from "@/lib/announcementColors";

// Circle swatch showing an announcement's colors ("A" in its text color on
// its background color). Used read-only (no hover state) in
// AnnouncementPicker's dropdown list, and with `editableOnHover` on
// AnnouncementColorPicker's own trigger (below) - where the "A" crossfades
// into a pencil on hover to hint "click to edit colors". Needs a `group`
// class on an ancestor.
export function AnnouncementColorSwatch({
  backgroundColor,
  textColor,
  size = 28,
  editableOnHover = false,
}: {
  backgroundColor: string | null;
  textColor: string | null;
  size?: number;
  editableOnHover?: boolean;
}) {
  const bg = backgroundColor ?? DEFAULT_ANNOUNCEMENT_BACKGROUND;
  const fg = textColor ?? DEFAULT_ANNOUNCEMENT_TEXT;
  return (
    <span
      className="relative inline-flex shrink-0 rounded-full ring-1 ring-black/10 items-center justify-center font-bold shadow-sm"
      style={{ width: size, height: size, backgroundColor: bg, color: fg, fontSize: size * 0.5 }}
    >
      {editableOnHover ? (
        <>
          <span className="transition-all duration-200 ease-out group-hover:opacity-0 group-hover:scale-50">
            A
          </span>
          <Pencil
            size={size * 0.55}
            className="absolute scale-50 opacity-0 transition-all duration-200 ease-out group-hover:scale-100 group-hover:opacity-100"
          />
        </>
      ) : (
        "A"
      )}
    </span>
  );
}

const POPOVER_WIDTH = 224;
const POPOVER_GAP = 8;
const POPOVER_ESTIMATED_HEIGHT = 180;

// Changes apply immediately (each edit calls `onChange`), so closing the
// popover - by the X, Escape, an outside click, or scrolling the page -
// never discards anything; it only hides the editor.
export function AnnouncementColorPicker({
  backgroundColor,
  textColor,
  onChange,
  disabled,
}: {
  backgroundColor: string | null;
  textColor: string | null;
  onChange: (colors: { backgroundColor: string | null; textColor: string | null }) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; placement: "above" | "below" } | null>(
    null
  );
  // Snapshot of the colors as they were when the popover opened, so the
  // close button can tell "edited, closing keeps it" (green check) apart
  // from "untouched, just dismissing" (plain x).
  const [openedWith, setOpenedWith] = useState<{ backgroundColor: string | null; textColor: string | null } | null>(
    null
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hasChanges =
    !!openedWith && (openedWith.backgroundColor !== backgroundColor || openedWith.textColor !== textColor);

  const bg = backgroundColor ?? DEFAULT_ANNOUNCEMENT_BACKGROUND;
  const fg = textColor ?? DEFAULT_ANNOUNCEMENT_TEXT;

  // Portaled to <body> and positioned from the trigger's live rect instead
  // of a plain `absolute` wrapper: AnnouncementsModal's row list scrolls
  // (`overflow-y-auto`), which was clipping the popover instead of letting
  // it float on top. Opens above the trigger by default - "on top" of the
  // row that spawned it - and only flips below when the trigger is too
  // close to the top of the viewport for that to fit.
  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const placement: "above" | "below" =
      rect.top >= POPOVER_ESTIMATED_HEIGHT + POPOVER_GAP ? "above" : "below";
    const top = placement === "above" ? rect.top - POPOVER_GAP : rect.bottom + POPOVER_GAP;
    const left = Math.max(8, Math.min(rect.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - 8));
    setPosition({ top, left, placement });
    setOpenedWith({ backgroundColor, textColor });
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    // The trigger's rect - and so the popover's anchor - goes stale the
    // moment its scroll container moves, so just close rather than track it.
    const onScroll = () => setIsOpen(false);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen]);

  const reset = () => onChange({ backgroundColor: null, textColor: null });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        title={t("announcements.colors")}
        className="group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transition-transform duration-150"
      >
        <AnnouncementColorSwatch backgroundColor={backgroundColor} textColor={textColor} editableOnHover />
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            data-announcement-color-popover
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: POPOVER_WIDTH,
              transform: position.placement === "above" ? "translateY(-100%)" : undefined,
            }}
            className="z-999 bg-app-surface text-foreground rounded-xl shadow-xl ring-1 ring-app-border p-3 flex flex-col gap-3"
          >
            <div className="flex items-center gap-2">
              <div
                className="flex-1 rounded-lg px-3 py-2 text-center text-xs font-bold uppercase tracking-wide"
                style={{ backgroundColor: bg, color: fg }}
              >
                {t("announcements.preview")}
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={hasChanges ? t("common.save") : t("common.close")}
                className={`p-1 rounded-full transition-colors duration-150 cursor-pointer ${
                  hasChanges ? "text-green-500 hover:bg-green-500/10" : "text-app-muted hover:text-foreground hover:bg-app-surface-hover"
                }`}
              >
                {hasChanges ? <Check size={14} /> : <X size={14} />}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <ColorRow
                label={t("announcements.backgroundColor")}
                value={bg}
                onChange={value => onChange({ backgroundColor: value, textColor })}
              />
              <ColorRow
                label={t("announcements.textColor")}
                value={fg}
                onChange={value => onChange({ backgroundColor, textColor: value })}
              />
            </div>

            <button
              type="button"
              onClick={reset}
              className="flex items-center justify-center gap-1.5 text-xs text-app-muted hover:text-brand transition-colors duration-150 cursor-pointer"
            >
              <RotateCcw size={12} />
              {t("announcements.resetColors")}
            </button>
          </div>,
          document.body
        )}
    </>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-app-muted">
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="size-7 rounded-md border border-app-border bg-app-control cursor-pointer p-0.5"
      />
      <span className="flex-1">{label}</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-20 text-[11px] font-mono border border-app-border bg-app-control text-foreground rounded-md px-1.5 py-1 outline-none focus:border-brand"
      />
    </label>
  );
}
